import { createSlice } from '@reduxjs/toolkit';

import { isListOver } from '@shared/functions/isListOver';

import {
  getReplays,
  getThread,
  getThreads,
  handleFindMessage,
  handleLoadChatData,
  updateThread,
} from './chatThunk';

import type { PayloadAction } from '@reduxjs/toolkit';
import type { MessageModel, ThreadModel } from '@shared/models/chat/chat.model';
import type { MediaType } from '@shared/models/media.model';
import type { MetaModel } from '@shared/models/meta.model';

type EditStateType = {
  message: string;
  files: MediaType[];
  messageId: number;
};

export interface ChatState {
  threads: {
    data: ThreadModel[];
    type: string;
    isStopPagination: boolean | number;
  };
  query: string;
  thread: {
    data: ThreadModel | null;
    isFetching: boolean;
    unreadReplies: number[];
    isFullLoading: boolean;
  };
  messages: {
    data: MessageModel[];
    typingUser: string[];
    isLoading: boolean;
  };
  reply: {
    message: MessageModel | null;
    data: MessageModel[];
    isStopPagination: boolean;
    page: number;
  };
  stubs: {
    isNoFound: boolean;
    isNoMessages: boolean;
  };
  pendingMessages: MessageModel[];
  pendingReplyMessages: MessageModel[];
  editState: {
    data: EditStateType | null;
  };
}

const initialState: ChatState = {
  threads: {
    data: [],
    type: '',
    isStopPagination: false,
  },
  query: '',
  messages: {
    data: [],
    typingUser: [],
    isLoading: false,
  },
  thread: {
    data: null,
    isFetching: false,
    unreadReplies: [],
    isFullLoading: true,
  },
  reply: {
    message: null,
    data: [],
    page: 1,
    isStopPagination: false,
  },
  stubs: {
    isNoFound: false,
    isNoMessages: false,
  },
  editState: {
    data: null,
  },
  pendingMessages: [],
  pendingReplyMessages: [],
};

export const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    setChatType: (state, action: PayloadAction<string>) => {
      state.threads.type = action.payload;
    },
    // Threads
    clearThreadsState: (state, action: PayloadAction<boolean | undefined>) => {
      state.threads.data = [];

      state.stubs.isNoFound = false;
      if (action.payload === undefined) {
        state.query = '';
      }
    },

    setFullThreadLoading(state, action: PayloadAction<boolean>) {
      state.thread.isFullLoading = action.payload;
    },

    setThreadsQuery(state, action: PayloadAction<string>) {
      state.query = action.payload;
    },
    setThreadPreloadData(state, action: PayloadAction<ThreadModel>) {
      state.thread.data = action.payload;
    },
    addThreadsItems(state, action: PayloadAction<ThreadModel>) {
      if (
        (action.payload.owner_id && state.threads.type === 'group') ||
        (!action.payload.owner_id && state.threads.type === 'personal')
      ) {
        const threadIndex = state.threads.data.findIndex((thread) => thread.id === action.payload.id);
        if (threadIndex !== 0 && threadIndex !== -1) {
          state.threads.data = [
            action.payload,
            ...state.threads.data.filter((thread) => thread.id !== action.payload.id),
          ];
          state.stubs.isNoFound = false;
        }
        if (threadIndex === -1) {
          state.stubs.isNoFound = false;
          state.threads.data = [action.payload, ...state.threads.data];
        }
      }
    },
    changeThreadCounter(state, action: PayloadAction<{ id: number; count: number }>) {
      state.threads.data = state.threads.data.map((item) => {
        if (item.id === action.payload.id) {
          return { ...item, count_unread_messages: action.payload.count };
        }
        return item;
      });
    },
    deleteThreadsItem(state, action: PayloadAction<number>) {
      const filtredData = state.threads.data.filter((thread) => thread.id !== action.payload);
      state.threads.data = filtredData;
      if (filtredData.length === 0) {
        state.stubs.isNoFound = true;
      }
    },
    clearChatState(state) {
      return { ...initialState, query: state.query };
    },
    // Thread
    setThreadFetching(state, action: PayloadAction<boolean>) {
      state.thread.isFetching = action.payload;
    },
    clearChatThreadState(state) {
      state.thread.data = null;
      state.thread.unreadReplies = [];
      state.stubs.isNoMessages = false;
    },
    updateThreadLastReadMessageId(
      state,
      action: PayloadAction<{ threadId: number; lastReadMessageId: number | null }>,
    ) {
      state.threads.data = state.threads.data.map((thread) => {
        if (thread.id === action.payload.threadId) {
          return { ...thread, last_read_message_id: action.payload.lastReadMessageId };
        }
        return thread;
      });
    },
    updateChatThread(state, action: PayloadAction<ThreadModel>) {
      state.thread.data = action.payload;
      state.threads.data = state.threads.data.map((thread) => {
        if (thread.id === action.payload.id) {
          return action.payload;
        }
        return thread;
      });
    },
    plusThreadCounter(state, action: PayloadAction<number>) {
      state.threads.data = state.threads.data.map((thread) => {
        if (thread.id === action.payload) {
          return {
            ...thread,
            count_unread_messages: thread.count_unread_messages ? thread.count_unread_messages + 1 : 1,
          };
        }
        return thread;
      });
    },
    subtractionThreadCounter(state, action: PayloadAction<number>) {
      state.threads.data = state.threads.data.map((thread) => {
        if (thread.id === action.payload) {
          return {
            ...thread,
            count_unread_messages: thread.count_unread_messages ? thread.count_unread_messages - 1 : 0,
          };
        }
        return thread;
      });
    },
    setThreadCounter(state, action: PayloadAction<{ id: number; count: number }>) {
      state.threads.data = state.threads.data.map((thread) => {
        if (thread.id === action.payload.id) {
          return {
            ...thread,
            count_unread_messages: action.payload.count,
          };
        }
        return thread;
      });
    },

    // Messages
    updateReplyMessage(state, action: PayloadAction<MessageModel>) {
      if (state.reply.message) {
        state.reply.message = action.payload;
      }
    },

    clearReplyState(state) {
      state.reply = initialState.reply;
    },

    setEditState(state, action: PayloadAction<EditStateType | null>) {
      state.editState.data = action.payload;
    },
    resetThreadChatQuery(state) {
      state.query = '';
    },
  },
  extraReducers: ({ addCase }) => {
    addCase(getThreads.fulfilled, (state, { payload }) => {
      if (payload.meta.current_page !== 1) {
        state.threads.data = [...state.threads.data, ...payload.data];
        state.threads.isStopPagination = isListOver({ meta: payload.meta as MetaModel });
      } else {
        state.threads.data = payload.data;
        state.threads.isStopPagination = isListOver({ meta: payload.meta as MetaModel });
      }

      state.stubs.isNoFound = payload.data.length === 0 && payload.meta.current_page === 1;
    });

    addCase(getThread.fulfilled, (state, { payload }) => {
      state.thread.data = payload;
    });

    addCase(updateThread.fulfilled, (state, { payload }) => {
      state.thread.data = state?.thread?.data?.id === payload.id ? payload : state.thread.data;
      state.threads.data = state.threads.data.map((thread) => {
        if (thread.id === payload.id) {
          return payload;
        }
        return thread;
      });
    });

    addCase(getReplays.fulfilled, (state, { payload }) => {
      state.reply.message = payload.message;
    });

    addCase(handleFindMessage.fulfilled, (state, { payload }) => {
      state.messages.data = payload.data;
    });
    addCase(handleLoadChatData.fulfilled, (state, { payload }) => {
      state.thread.data = payload.thread;
    });
  },
});

export const {
  clearThreadsState,
  addThreadsItems,
  deleteThreadsItem,
  subtractionThreadCounter,
  setThreadsQuery,
  clearChatState,
  clearChatThreadState,
  updateReplyMessage,
  updateThreadLastReadMessageId,
  clearReplyState,
  setThreadCounter,
  setChatType,
  changeThreadCounter,
  setThreadPreloadData,
  setEditState,
  resetThreadChatQuery,
} = chatSlice.actions;
export default chatSlice.reducer;
